import { useState, useRef, useCallback } from 'react'
import { policyService } from '../services/policy'
import type { PolicyExtractionResult, PolicyFieldOverride } from '../services/policy'
import type { PolicyResponse } from '../../../types'
import { FileText, CheckCircle, AlertTriangle, X, Upload, Pencil, Loader2 } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { authService } from '../../identity/services/auth'

const CONFIDENCE_THRESHOLD = 0.85

interface IssuePolicyModalProps {
  isOpen: boolean
  leadId: number
  customerName: string
  onClose: () => void
  onIssued: (policy: PolicyResponse) => void
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ConfidenceBadge({ score }: { score: number }) {
  if (score >= CONFIDENCE_THRESHOLD) return null
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 11, padding: '1px 6px', borderRadius: 999,
        background: 'var(--warning-bg, #fef3c7)',
        color: 'var(--warning-text, #92400e)', marginLeft: 6,
      }}
    >
      <AlertTriangle style={{ width: 10, height: 10 }} />
      {Math.round(score * 100)}% confident
    </span>
  )
}

function ReadonlyField({
  label,
  value,
  confidence,
  overrideValue,
  overrideActive,
  onOverride,
  onChange,
  placeholder,
  type = 'text',
  required = true,
}: {
  label: string
  value: string
  confidence?: number
  overrideValue: string
  overrideActive: boolean
  onOverride: () => void
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  required?: boolean
}) {
  const lowConfidence = confidence !== undefined && confidence < CONFIDENCE_THRESHOLD

  return (
    <div className="form-group">
      <label className="form-label">
        {label}{required && <span style={{ color: 'var(--danger-text)' }}> *</span>}
        {confidence !== undefined && <ConfidenceBadge score={confidence} />}
      </label>

      {overrideActive ? (
        <input
          type={type}
          className="input"
          value={overrideValue}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
        />
      ) : (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px', borderRadius: 6,
            background: lowConfidence ? 'var(--warning-bg, #fef3c7)' : 'var(--bg-muted)',
            border: `1px solid ${lowConfidence ? 'var(--warning-border, #fcd34d)' : 'var(--border-color)'}`,
            fontSize: 14, color: 'var(--text-primary)',
          }}
        >
          <span style={{ fontWeight: 500 }}>{value || '—'}</span>
          <button
            type="button"
            onClick={onOverride}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: 'var(--text-secondary)', padding: '2px 6px',
              borderRadius: 4,
            }}
            title="Override this extracted value"
          >
            <Pencil style={{ width: 11, height: 11 }} /> Override
          </button>
        </div>
      )}

      {lowConfidence && !overrideActive && (
        <p className="form-hint" style={{ color: 'var(--warning-text, #92400e)' }}>
          Low confidence — please verify this value before issuing
        </p>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function IssuePolicyModal({
  isOpen,
  leadId,
  customerName,
  onClose,
  onIssued,
}: IssuePolicyModalProps) {
  // Phase: 'upload' | 'review'
  const [phase, setPhase]           = useState<'upload' | 'review'>('upload')
  const [policyPdf, setPolicyPdf]   = useState<File | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [extraction, setExtraction] = useState<PolicyExtractionResult | null>(null)
  const [aiEnabled, setAiEnabled]   = useState<boolean | null>(null)  // null = not yet checked
  const fileInputRef                = useRef<HTMLInputElement>(null)

  // Extracted / form field values
  const [policyNumber, setPolicyNumber]         = useState('')
  const [insuranceCompany, setInsuranceCompany] = useState('')
  const [premiumAmount, setPremiumAmount]       = useState('')
  const [odDiscount, setOdDiscount]             = useState('')

  // Override tracking: field name → original extracted value
  const [overrides, setOverrides]         = useState<Record<string, string>>({})
  // Which fields are currently in edit mode
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set())

  // Payment (always manual)
  const [paymentMode, setPaymentMode]           = useState<'CASH' | 'ONLINE' | 'CHEQUE' | 'UPI'>('ONLINE')
  const [paymentReference, setPaymentReference] = useState('')

  const [error, setError]       = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [issued, setIssued]     = useState<PolicyResponse | null>(null)

  // ── Override helpers (hooks must be above any conditional return) ──────────

  const startOverride = useCallback((field: string, extractedValue: string) => {
    setOverrides(prev => ({ ...prev, [field]: extractedValue }))
    setEditingFields(prev => new Set(prev).add(field))
  }, [])

  const getOverrideProps = (
    field: string,
    extractedValue: string,
    currentValue: string,
    setValue: (v: string) => void,
    confidence?: number,
  ) => ({
    confidence,
    overrideValue: currentValue,
    overrideActive: editingFields.has(field),
    onOverride: () => startOverride(field, extractedValue),
    onChange: setValue,
  })

  if (!isOpen) return null

  // ── File selected ──────────────────────────────────────────────────────────

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.pdf')) {
      setError('Only PDF files are accepted')
      return
    }
    setError('')
    setPolicyPdf(file)

    // Check ai_extraction flag (first time only)
    let aiOn = aiEnabled
    if (aiOn === null) {
      try {
        const user = await authService.fetchMe()
        aiOn = !!user.tenant_feature_flags?.['ai_extraction_enabled']
        setAiEnabled(aiOn)
      } catch {
        aiOn = false
        setAiEnabled(false)
      }
    }

    setPhase('review')
    if (aiOn) {
      setExtracting(true)
      try {
        const result = await policyService.extractPdf(leadId, file)
        setExtraction(result)
        if (!result.extraction_failed) {
          setPolicyNumber(result.policy_number ?? '')
          setInsuranceCompany(result.insurance_company ?? '')
          setPremiumAmount(result.premium_amount != null ? String(result.premium_amount) : '')
          setOdDiscount(result.od_discount_percentage != null ? String(result.od_discount_percentage) : '')
        }
      } catch {
        setExtraction({ extraction_failed: true, extraction_error: 'Extraction request failed', policy_number: null, insurance_company: null, premium_amount: null, od_discount_percentage: null, confidence: {} })
      } finally {
        setExtracting(false)
      }
    }
  }


  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleIssue = async () => {
    setError('')
    if (!policyNumber.trim() || !insuranceCompany.trim() || !premiumAmount || !paymentReference.trim()) {
      setError('Policy Number, Insurance Company, Premium, and Payment Reference are required')
      return
    }
    if (!policyPdf) {
      setError('Policy PDF is required')
      return
    }
    const pmt = parseFloat(premiumAmount)
    if (isNaN(pmt) || pmt <= 0) {
      setError('Premium amount must be a positive number')
      return
    }
    if (paymentMode === 'CASH' && pmt > 10000) {
      setError('Cash payments above ₹10,000 require Insurance Head / Dealer Principal pre-approval')
      return
    }
    const disc = parseFloat(odDiscount) || 0
    if (disc > 30) {
      setError('OD discount above 30% requires Insurance Head / Dealer Principal approval before issuance')
      return
    }

    // Build field_overrides list for audit logging
    const fieldOverrides: PolicyFieldOverride[] = []
    for (const [field, extractedValue] of Object.entries(overrides)) {
      const currentValues: Record<string, string> = {
        policy_number: policyNumber,
        insurance_company: insuranceCompany,
        premium_amount: premiumAmount,
        od_discount_percentage: odDiscount,
      }
      const submitted = currentValues[field] ?? ''
      if (submitted !== extractedValue) {
        fieldOverrides.push({ field, extracted_value: extractedValue, submitted_value: submitted })
      }
    }

    setSubmitting(true)
    try {
      // If AI extraction was disabled, upload PDF separately first
      if (!aiEnabled) {
        await policyService.uploadPdf(leadId, policyPdf)
      }

      const result = await policyService.issue({
        lead_id: leadId,
        policy_number: policyNumber.trim(),
        insurance_company: insuranceCompany.trim(),
        premium_amount: pmt,
        od_discount_percentage: disc,
        payment_mode: paymentMode,
        payment_reference: paymentReference.trim(),
        field_overrides: fieldOverrides,
      })
      setIssued(result)
      onIssued(result)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to issue policy')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setPhase('upload')
    setPolicyPdf(null)
    setExtracting(false)
    setExtraction(null)
    setAiEnabled(null)
    setPolicyNumber('')
    setInsuranceCompany('')
    setPremiumAmount('')
    setOdDiscount('')
    setOverrides({})
    setEditingFields(new Set())
    setPaymentMode('ONLINE')
    setPaymentReference('')
    setError('')
    setIssued(null)
    onClose()
  }

  const conf = extraction?.confidence ?? {}

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ModalOverlay onClose={handleClose}>
      <div className="modal-panel-flex" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>

        {/* ── Success ─────────────────────────────────────────────────────── */}
        {issued ? (
          <div className="modal-flex-body flex flex-col items-center justify-center space-y-5 py-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto"
              style={{ background: 'var(--success-bg)' }}
            >
              <CheckCircle className="h-8 w-8" style={{ color: 'var(--success-text)' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Policy Issued</h2>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{customerName}</p>
            </div>
            <div
              className="text-left space-y-2 rounded-lg px-4 py-3 w-full"
              style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)' }}
            >
              {[
                ['Policy No', issued.policy_number],
                ['Insurer',   issued.insurance_company],
                ['Premium',   issued.premium_amount != null ? `₹${issued.premium_amount.toLocaleString()}` : '—'],
              ].map(([label, val]) => (
                <p key={label} className="text-sm">
                  <span style={{ color: 'var(--text-tertiary)' }}>{label}: </span>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{val}</span>
                </p>
              ))}
              {issued.policy_verification_status && issued.policy_verification_status !== 'SKIPPED' && (
                <p className="text-sm flex items-center gap-1.5 mt-1">
                  {issued.policy_verification_status === 'VERIFIED' ? (
                    <><CheckCircle className="h-4 w-4 shrink-0" style={{ color: 'var(--success-text)' }} />
                    <span style={{ color: 'var(--success-text)' }}>Verified with insurer</span></>
                  ) : issued.policy_verification_status === 'INVALID' ? (
                    <><AlertTriangle className="h-4 w-4 shrink-0" style={{ color: 'var(--danger-text)' }} />
                    <span style={{ color: 'var(--danger-text)' }}>Verification failed — fraud alert raised</span></>
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)' }}>Verification: {issued.policy_verification_status}</span>
                  )}
                </p>
              )}
            </div>
            <button className="btn-primary w-full" onClick={handleClose}>Done</button>
          </div>

        ) : (
          <>
            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="modal-flex-header">
              <div className="flex items-center gap-2.5">
                <FileText className="h-5 w-5 shrink-0" style={{ color: 'var(--brand-600)' }} />
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Issue Policy</h2>
                  <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{customerName}</p>
                </div>
              </div>
              <button className="btn-icon" onClick={handleClose} disabled={submitting || extracting}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="modal-flex-body space-y-4 pt-0">
              {error && (
                <div className="alert-danger mb-1">
                  <AlertTriangle className="h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}

              {/* ── Phase 1: Upload ──────────────────────────────────── */}
              {phase === 'upload' && (
                <div className="space-y-4">
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Upload the policy PDF to begin. Details will be extracted automatically if AI extraction is enabled for your account.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f) }}
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed var(--border-color)', borderRadius: 10,
                      padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
                    }}
                  >
                    <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                      Click to upload policy PDF
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>PDF only</p>
                  </div>
                </div>
              )}

              {/* ── Extracting spinner ──────────────────────────────── */}
              {phase === 'review' && extracting && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin" style={{ color: 'var(--brand-primary)' }} />
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                    Extracting policy details…
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    {policyPdf?.name}
                  </p>
                </div>
              )}

              {/* ── Phase 2: Review & Issue ──────────────────────────── */}
              {phase === 'review' && !extracting && (
                <div className="space-y-4">

                  {/* PDF attached */}
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 8,
                      background: 'var(--bg-muted)', border: '1px solid var(--border-color)',
                      fontSize: 13,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{policyPdf?.name}</span>
                    </div>
                    <button
                      className="btn-icon"
                      onClick={() => { setPolicyPdf(null); setExtraction(null); setPhase('upload') }}
                      title="Change PDF"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Extraction failure banner */}
                  {extraction?.extraction_failed && (
                    <div className="alert-warning" style={{ fontSize: 13 }}>
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>
                        AI extraction failed — please fill in the details manually.
                        {extraction.extraction_error ? ` (${extraction.extraction_error})` : ''}
                      </span>
                    </div>
                  )}

                  {/* Extraction source label */}
                  {extraction && !extraction.extraction_failed && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                      Fields below were extracted from the PDF. Fields with low confidence are highlighted — verify before issuing.
                    </p>
                  )}

                  {/* ── Policy details ─────────────────────────────── */}
                  <div
                    style={{
                      borderRadius: 8, border: '1px solid var(--border-color)',
                      padding: '14px 14px 10px',
                    }}
                  >
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                      Policy Details
                    </p>
                    <div className="space-y-3">

                      {extraction && !extraction.extraction_failed ? (
                        <>
                          <ReadonlyField
                            label="Policy Number"
                            value={policyNumber}
                            {...getOverrideProps('policy_number', extraction.policy_number ?? '', policyNumber, setPolicyNumber, conf.policy_number)}
                            placeholder="e.g. OG-24-2101-1801-00012345"
                          />
                          <ReadonlyField
                            label="Insurance Company"
                            value={insuranceCompany}
                            {...getOverrideProps('insurance_company', extraction.insurance_company ?? '', insuranceCompany, setInsuranceCompany, conf.insurance_company)}
                            placeholder="e.g. HDFC ERGO"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <ReadonlyField
                              label="Premium (₹)"
                              value={premiumAmount ? `₹${parseFloat(premiumAmount).toLocaleString('en-IN')}` : '—'}
                              {...getOverrideProps('premium_amount', extraction.premium_amount != null ? String(extraction.premium_amount) : '', premiumAmount, setPremiumAmount, conf.premium_amount)}
                              placeholder="e.g. 14500"
                              type="number"
                            />
                            <ReadonlyField
                              label="OD Discount (%)"
                              value={odDiscount ? `${odDiscount}%` : '0%'}
                              {...getOverrideProps('od_discount_percentage', extraction.od_discount_percentage != null ? String(extraction.od_discount_percentage) : '0', odDiscount, setOdDiscount, conf.od_discount_percentage)}
                              placeholder="e.g. 20"
                              type="number"
                              required={false}
                            />
                          </div>
                          {odDiscount && parseFloat(odDiscount) > 30 && (
                            <p className="form-hint" style={{ color: 'var(--warning-text)' }}>
                              ⚠ Above 30% — requires IH / DP approval
                            </p>
                          )}
                        </>
                      ) : (
                        /* Manual entry when extraction disabled or failed */
                        <>
                          <div className="form-group">
                            <label className="form-label">Policy Number <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                            <input type="text" className="input" value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} placeholder="e.g. OG-24-2101-1801-00012345" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Insurance Company <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                            <input type="text" className="input" value={insuranceCompany} onChange={e => setInsuranceCompany(e.target.value)} placeholder="e.g. Bajaj Allianz, HDFC ERGO" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="form-group">
                              <label className="form-label">Premium (₹) <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                              <input type="number" className="input" value={premiumAmount} onChange={e => setPremiumAmount(e.target.value)} min="0" placeholder="8750" />
                            </div>
                            <div className="form-group">
                              <label className="form-label">OD Discount (%)</label>
                              <input type="number" className="input" value={odDiscount} onChange={e => setOdDiscount(e.target.value)} min="0" max="100" placeholder="0" />
                              {odDiscount && parseFloat(odDiscount) > 30 && (
                                <p className="form-hint" style={{ color: 'var(--warning-text)' }}>⚠ Above 30% — requires IH / DP approval</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── Payment details (always manual) ────────────── */}
                  <div
                    style={{
                      borderRadius: 8, border: '1px solid var(--border-color)',
                      padding: '14px 14px 10px',
                    }}
                  >
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                      Payment Details
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="form-label">Payment Mode <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                        <select className="input" value={paymentMode} onChange={e => setPaymentMode(e.target.value as typeof paymentMode)}>
                          <option value="ONLINE">Online</option>
                          <option value="UPI">UPI</option>
                          <option value="CHEQUE">Cheque</option>
                          <option value="CASH">Cash</option>
                        </select>
                        {paymentMode === 'CASH' && (
                          <p className="form-hint" style={{ color: 'var(--warning-text)' }}>⚠ Cash above ₹10,000 requires pre-approval</p>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Payment Reference <span style={{ color: 'var(--danger-text)' }}>*</span></label>
                        <input
                          type="text"
                          className="input"
                          value={paymentReference}
                          onChange={e => setPaymentReference(e.target.value)}
                          placeholder="UTR / cheque no. / receipt ID"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Overrides summary */}
                  {Object.keys(overrides).length > 0 && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      <AlertTriangle style={{ display: 'inline', width: 12, height: 12, marginRight: 4, color: 'var(--warning-text)' }} />
                      {Object.keys(overrides).length} extracted field{Object.keys(overrides).length > 1 ? 's' : ''} overridden — changes will be recorded in the audit log.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── Footer ────────────────────────────────────────────────── */}
            <div className="modal-flex-footer">
              <button className="btn-secondary" onClick={handleClose} disabled={submitting || extracting}>
                Cancel
              </button>
              {phase === 'review' && !extracting && (
                <button className="btn-primary" onClick={handleIssue} disabled={submitting}>
                  {submitting ? 'Issuing…' : 'Issue Policy'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  )
}
